// core/action/barcodeActions.js
import { barcodeAPI } from './api';
import { toast } from 'react-toastify';

export const barcodeActions = {
    // =====================================================
    // BARCODE GENERATION & MANAGEMENT
    // =====================================================

    /**
     * Generate barcode for a single product
     * @param {number} productId - Product ID
     * @returns {Promise<Object>} Result with barcode data
     */
    generateBarcode: async (productId) => {
        try {
            if (!productId) {
                return { success: false, message: 'Product ID is required' };
            }

            const response = await barcodeAPI.generateBarcode(productId);

            if (response && response.success) {
                toast.success(response.message || 'Barcode generated successfully');
                return {
                    success: true,
                    data: response.data,
                    message: response.message
                };
            } else {
                toast.error(response?.message || 'Failed to generate barcode');
                return {
                    success: false,
                    message: response?.message || 'Failed to generate barcode'
                };
            }
        } catch (error) {
            console.error('Error generating barcode:', error);
            toast.error(error.message || 'Failed to generate barcode');
            return {
                success: false,
                message: error.message || 'Failed to generate barcode'
            };
        }
    },

    /**
     * Generate multiple barcodes for products
     * @param {Array} productIds - Array of product IDs
     * @returns {Promise<Object>} Result with generated barcodes
     */
    generateMultipleBarcodes: async (productIds) => {
        try {
            if (!productIds || productIds.length === 0) {
                return { success: false, message: 'No products selected' };
            }

            const results = [];
            let successCount = 0;
            let failCount = 0;

            // Generate barcodes sequentially
            for (const productId of productIds) {
                try {
                    const response = await barcodeAPI.generateBarcode(productId);
                    if (response && response.success) {
                        successCount++;
                        results.push({
                            productId,
                            success: true,
                            data: response.data
                        });
                    } else {
                        failCount++;
                        results.push({
                            productId,
                            success: false,
                            message: response?.message
                        });
                    }
                } catch (error) {
                    failCount++;
                    results.push({
                        productId,
                        success: false,
                        message: error.message
                    });
                }
            }

            toast.success(`Generated ${successCount} barcodes, ${failCount} failed`);
            return {
                success: true,
                data: {
                    results,
                    successCount,
                    failCount,
                    total: productIds.length
                },
                message: `Generated ${successCount} barcodes`
            };
        } catch (error) {
            console.error('Error generating multiple barcodes:', error);
            toast.error(error.message || 'Failed to generate barcodes');
            return {
                success: false,
                message: error.message || 'Failed to generate barcodes'
            };
        }
    },

    /**
     * Get barcodes for products with pagination and filters
     * @param {Object} params - Search parameters
     * @returns {Promise<Object>} Result with barcodes data
     */
    getBarcodes: async (params = {}) => {
        try {
            const response = await barcodeAPI.getBarcodes(params);

            if (response && response.success) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || 1
                };
            } else {
                return {
                    success: false,
                    message: response?.message || 'Failed to fetch barcodes',
                    data: []
                };
            }
        } catch (error) {
            console.error('Error fetching barcodes:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch barcodes',
                data: []
            };
        }
    },

    // =====================================================
    // QR CODE GENERATION
    // =====================================================

    /**
     * Generate QR code for a product
     * @param {number} productId - Product ID
     * @returns {Promise<Object>} Result with QR code data
     */
    generateQRCode: async (productId) => {
        try {
            if (!productId) {
                return { success: false, message: 'Product ID is required' };
            }

            const response = await barcodeAPI.generateQR(productId);

            if (response && response.success) {
                toast.success(response.message || 'QR code generated successfully');
                return {
                    success: true,
                    data: response.data,
                    message: response.message
                };
            } else {
                toast.error(response?.message || 'Failed to generate QR code');
                return {
                    success: false,
                    message: response?.message || 'Failed to generate QR code'
                };
            }
        } catch (error) {
            console.error('Error generating QR code:', error);
            toast.error(error.message || 'Failed to generate QR code');
            return {
                success: false,
                message: error.message || 'Failed to generate QR code'
            };
        }
    },

    /**
     * Generate GST QR code for an invoice
     * @param {number} saleId - Sale/Invoice ID
     * @returns {Promise<Object>} Result with GST QR code data
     */
    generateGSTQR: async (saleId) => {
        try {
            if (!saleId) {
                return { success: false, message: 'Sale ID is required' };
            }

            const response = await barcodeAPI.generateGSTQR(saleId);

            if (response && response.success) {
                toast.success(response.message || 'GST QR code generated successfully');
                return {
                    success: true,
                    data: response.data,
                    message: response.message
                };
            } else {
                toast.error(response?.message || 'Failed to generate GST QR code');
                return {
                    success: false,
                    message: response?.message || 'Failed to generate GST QR code'
                };
            }
        } catch (error) {
            console.error('Error generating GST QR code:', error);
            toast.error(error.message || 'Failed to generate GST QR code');
            return {
                success: false,
                message: error.message || 'Failed to generate GST QR code'
            };
        }
    },

    // =====================================================
    // PRINT JOB MANAGEMENT
    // =====================================================

    /**
     * Create a print job for multiple products
     * @param {Object} jobData - Print job data
     * @returns {Promise<Object>} Result with print job data
     */
    createPrintJob: async (jobData) => {
        try {
            if (!jobData.product_ids || jobData.product_ids.length === 0) {
                return { success: false, message: 'No products selected' };
            }

            if (!jobData.copies || jobData.copies < 1) {
                jobData.copies = 1;
            }

            const response = await barcodeAPI.createPrintJob(jobData);

            if (response && response.success) {
                toast.success(response.message || 'Print job created successfully');
                return {
                    success: true,
                    data: response.data,
                    message: response.message
                };
            } else {
                toast.error(response?.message || 'Failed to create print job');
                return {
                    success: false,
                    message: response?.message || 'Failed to create print job'
                };
            }
        } catch (error) {
            console.error('Error creating print job:', error);
            toast.error(error.message || 'Failed to create print job');
            return {
                success: false,
                message: error.message || 'Failed to create print job'
            };
        }
    },

    /**
     * Get print jobs with pagination and filters
     * @param {Object} params - Search parameters
     * @returns {Promise<Object>} Result with print jobs data
     */
    getPrintJobs: async (params = {}) => {
        try {
            const response = await barcodeAPI.getPrintJobs(params);

            if (response && response.success) {
                return {
                    success: true,
                    data: response.data || [],
                    total: response.total || 0,
                    page: response.page || 1,
                    totalPages: response.total_pages || 1
                };
            } else {
                return {
                    success: false,
                    message: response?.message || 'Failed to fetch print jobs',
                    data: []
                };
            }
        } catch (error) {
            console.error('Error fetching print jobs:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch print jobs',
                data: []
            };
        }
    },

    /**
     * Get single print job details
     * @param {number} jobId - Print job ID
     * @returns {Promise<Object>} Result with job details
     */
    getPrintJobDetails: async (jobId) => {
        try {
            if (!jobId) {
                return { success: false, message: 'Job ID is required' };
            }

            const response = await barcodeAPI.getPrintJobDetails(jobId);

            if (response && response.success) {
                return {
                    success: true,
                    data: response.data,
                    message: response.message
                };
            } else {
                return {
                    success: false,
                    message: response?.message || 'Failed to fetch job details'
                };
            }
        } catch (error) {
            console.error('Error fetching job details:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch job details'
            };
        }
    },

    /**
     * Cancel a print job
     * @param {number} jobId - Print job ID
     * @returns {Promise<Object>} Result with cancellation status
     */
    cancelPrintJob: async (jobId) => {
        try {
            if (!jobId) {
                return { success: false, message: 'Job ID is required' };
            }

            const response = await barcodeAPI.cancelPrintJob(jobId);

            if (response && response.success) {
                toast.success(response.message || 'Print job cancelled successfully');
                return {
                    success: true,
                    message: response.message
                };
            } else {
                toast.error(response?.message || 'Failed to cancel print job');
                return {
                    success: false,
                    message: response?.message || 'Failed to cancel print job'
                };
            }
        } catch (error) {
            console.error('Error cancelling print job:', error);
            toast.error(error.message || 'Failed to cancel print job');
            return {
                success: false,
                message: error.message || 'Failed to cancel print job'
            };
        }
    },

    // =====================================================
    // BARCODE SETTINGS
    // =====================================================

    /**
     * Get barcode settings
     * @returns {Promise<Object>} Result with settings data
     */
    getBarcodeSettings: async () => {
        try {
            const response = await barcodeAPI.getBarcodeSettings();

            if (response && response.success) {
                return {
                    success: true,
                    data: response.data || {}
                };
            } else {
                return {
                    success: false,
                    message: response?.message || 'Failed to fetch settings',
                    data: {}
                };
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch settings',
                data: {}
            };
        }
    },

    /**
     * Update barcode settings
     * @param {Object} settingsData - Settings data to update
     * @returns {Promise<Object>} Result with updated settings
     */
    updateBarcodeSettings: async (settingsData) => {
        try {
            const response = await barcodeAPI.updateBarcodeSettings(settingsData);

            if (response && response.success) {
                toast.success(response.message || 'Settings updated successfully');
                return {
                    success: true,
                    data: response.data,
                    message: response.message
                };
            } else {
                toast.error(response?.message || 'Failed to update settings');
                return {
                    success: false,
                    message: response?.message || 'Failed to update settings'
                };
            }
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error(error.message || 'Failed to update settings');
            return {
                success: false,
                message: error.message || 'Failed to update settings'
            };
        }
    },

    // =====================================================
    // UTILITY FUNCTIONS
    // =====================================================

    /**
     * Download barcode image
     * @param {string} imagePath - Path to barcode image
     * @param {string} filename - Filename for download
     */
    downloadBarcode: (imagePath, filename = 'barcode.png') => {
        try {
            const link = document.createElement('a');
            link.href = imagePath;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success('Download started');
        } catch (error) {
            console.error('Error downloading barcode:', error);
            toast.error('Failed to download barcode');
        }
    },

    /**
     * Print barcode image
     * @param {string} imagePath - Path to barcode image
     */
    printBarcode: (imagePath) => {
        try {
            const printWindow = window.open(imagePath, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            } else {
                toast.error('Popup blocked. Please allow popups for this site.');
            }
        } catch (error) {
            console.error('Error printing barcode:', error);
            toast.error('Failed to print barcode');
        }
    },

    /**
     * Get barcode statistics
     * @returns {Promise<Object>} Result with statistics
     */
    getBarcodeStatistics: async () => {
        // This would need a backend endpoint; for now, return mock data
        return {
            success: true,
            data: {
                totalProductsWithBarcodes: 0,
                totalPrintJobs: 0,
                totalBarcodesPrinted: 0,
                recentJobs: []
            }
        };
    }
};

// =====================================================
// HELPER FUNCTIONS (EXPORTED SEPARATELY)
// =====================================================

/**
 * Validate barcode number
 * @param {string} barcode - Barcode number to validate
 * @param {string} type - Barcode type (CODE128, EAN13, etc.)
 * @returns {Object} Validation result
 */
export const validateBarcode = (barcode, type = 'CODE128') => {
    if (!barcode) {
        return { valid: false, message: 'Barcode is required' };
    }

    switch (type) {
        case 'EAN13': {
            // EAN-13 must be 13 digits
            const eanRegex = /^\d{13}$/;
            if (!eanRegex.test(barcode)) {
                return { valid: false, message: 'EAN-13 must be exactly 13 digits' };
            }
            // Validate check digit
            if (!validateEAN13Checksum(barcode)) {
                return { valid: false, message: 'Invalid EAN-13 checksum' };
            }
            break;
        }
        case 'CODE128': {
            // CODE128 can contain any characters, but we'll limit to reasonable length
            if (barcode.length < 4 || barcode.length > 50) {
                return { valid: false, message: 'CODE128 should be between 4-50 characters' };
            }
            break;
        }
        case 'UPC': {
            // UPC must be 12 digits
            const upcRegex = /^\d{12}$/;
            if (!upcRegex.test(barcode)) {
                return { valid: false, message: 'UPC must be exactly 12 digits' };
            }
            break;
        }
        default: {
            // Generic validation
            if (barcode.length < 3 || barcode.length > 100) {
                return { valid: false, message: 'Barcode length should be between 3-100 characters' };
            }
        }
    }

    return { valid: true, message: 'Valid barcode format' };
};

// Helper function for EAN-13 checksum validation
function validateEAN13Checksum(barcode) {
    if (barcode.length !== 13) return false;
    
    let sum = 0;
    for (let i = 0; i < 12; i++) {
        const digit = parseInt(barcode.charAt(i));
        sum += (i % 2 === 0) ? digit : digit * 3;
    }
    
    const checksum = (10 - (sum % 10)) % 10;
    return checksum === parseInt(barcode.charAt(12));
}

export default barcodeActions;